import { useMemo } from 'react';

import { addDays, subDays } from 'date-fns';

import { DropdownSearchItem } from '@/modules/common/components/molecules/selectors/dropdown';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';
import { normalizeToStartOfDayUTC } from '@/utils/date-formatters';

import { getTimeTrackerProjects } from '../api/time-tracker-projects.api';
import { NON_ARCHIVED_PROJECT_STATUSES } from '../types/dto/request/find-short-projects-request';

/** How far around the anchor date to check for existing bookings (see API description). */
const BOOKING_WINDOW_DAYS = 7;

export const TIME_TRACKER_PROJECTS_QUERY_KEY_ROOT = 'time-tracker-projects';

/**
 * How long fetched pages stay fresh before a background refetch. Booked/project data
 * doesn't change often relative to how many times the picker can be opened in a
 * session, and assignment/project mutations invalidate this cache explicitly (see
 * `invalidateTimeTrackerProjectsQueries` in `@/modules/common/utils/query-invalidation`),
 * so it's safe to cache for a while and cut down on redundant requests.
 */
export const TIME_TRACKER_PROJECTS_STALE_TIME_MS = 5 * 60 * 1000;

interface UseTimeTrackerProjectsResult {
  queryKey: string[];
  staleTime: number;
  loadItems: (cursor?: number | null) => Promise<PaginatedResponse<DropdownSearchItem>>;
  searchItems: (
    searchTerm: string,
    cursor?: number | null
  ) => Promise<PaginatedResponse<DropdownSearchItem>>;
}

/**
 * Builds the query key, stale time, and cursor loaders for the Time Tracking project
 * picker (`/api/projects/time-tracker/search`), centered on `anchorDate` — the date
 * the entry is being logged for, which decides the ± window used for the booking check.
 */
export const useTimeTrackerProjects = (anchorDate: Date): UseTimeTrackerProjectsResult => {
  const anchorDateKey = normalizeToStartOfDayUTC(anchorDate).toISOString();

  const filter = useMemo(
    () => ({
      status: NON_ARCHIVED_PROJECT_STATUSES,
      startDate: normalizeToStartOfDayUTC(subDays(anchorDate, BOOKING_WINDOW_DAYS)).toISOString(),
      endDate: normalizeToStartOfDayUTC(addDays(anchorDate, BOOKING_WINDOW_DAYS)).toISOString(),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [anchorDateKey]
  );

  const queryKey = useMemo(
    () => [TIME_TRACKER_PROJECTS_QUERY_KEY_ROOT, anchorDateKey],
    [anchorDateKey]
  );

  return useMemo(
    () => ({
      queryKey,
      staleTime: TIME_TRACKER_PROJECTS_STALE_TIME_MS,
      loadItems: cursor => getTimeTrackerProjects({ filter, pagination: { cursor } }),
      searchItems: (searchTerm, cursor) =>
        getTimeTrackerProjects({
          filter: { ...filter, searchString: searchTerm },
          pagination: { cursor },
        }),
    }),
    [queryKey, filter]
  );
};
